/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon,
  Badge,
  Card, DetailStack, Grid, HStack, ICON_ACCOUNT_OFF_OUTLINE, ICON_BRIEFCASE_OUTLINE, ICON_EYE_OUTLINE, Popover, Stack, Text,
  UserPreview,
} from '@doveit/bricks';
import { isFuture } from 'date-fns';
import { pluralizeWord } from '@doveit/hammer';
import {
  HRInterview, HRRecall, JobApplicationSales, SerpJobApplication,
} from '../../../providers/api/dtos';
import { JobApplicationType } from '../../../domain/types';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import UpdateJobApplicationStatus from '../update-job-application-status/UpdateJobApplicationStatus';
import { ViewAgentSmallPreview } from '../../../agent/components';
import * as styles from './ViewSerpJobApplicationCard.style';
import ApplicantMissedCallRegistryList from '../../components/applicant-missed-call-registry-list/ApplicantMissedCallRegistryList';
import ViewApplicantPopoverPreview from '../view-applicant-popover-preview/ViewApplicantPopoverPreview';
import TextPreview from '../../../components/text-preview/TextPreview';
import DateViewer from '../../../components/date-viewer/DateViewer';
import UpdateHRRecallStatus from '../../../hr/containers/update-hr-recall-status/UpdateHRRecallStatus';
import UpdateHRInterviewStatus from '../../../hr/containers/update-hr-interview-status/UpdateHRInterviewStatus';
import ViewHRInterviewPopoverPreview from '../../../hr/containers/view-hr-interview-popover-preview/ViewHRInterviewPopoverPreview';
import { useJobApplicationSerpData } from '../../hooks/use-job-application-serp-data/useJobApplicationSerpData';
import { ThemeIcons } from '../../../theme/icons';

export interface ViewSerpJobApplicationCardProps extends React.AriaAttributes {
  serpJobApplication: SerpJobApplication,
  onJobApplicationUpdate?: VoidFunction,
  onInterviewUpdate?: (updatedInterview: HRInterview) => void,
  onRecallUpdate?: (updatedRecall: HRRecall) => void,
  canManageCalendarEvents?: boolean,
}

export const ViewSerpJobApplicationCard: React.FC<ViewSerpJobApplicationCardProps> = ({
  serpJobApplication,
  onJobApplicationUpdate,
  onInterviewUpdate,
  onRecallUpdate,
}) => {
  const {
    jobApplication,
    applicant,
    jobPositionData,
    calendarEvent,
    note,
    showMissedCalls,
    supervisorName,
    supervisorPicture,
    assigneeName,
    goToJobApplicationPage,
  } = useJobApplicationSerpData(serpJobApplication);

  const { isFranchising, provinces } = React.useMemo(() => {
    if (jobApplication.applicationType === JobApplicationType.SALES) {
      const salesJobApplication = jobApplication as JobApplicationSales;
      return {
        isFranchising: salesJobApplication.isFranchising,
        provinces: salesJobApplication.provinces,
      };
    }
    return {
      isFranchising: undefined,
      provinces: undefined,
    };
  }, [jobApplication]);

  return (
    <Card>
      <Card.Header
        primaryActions={[
          showMissedCalls && (
            <div aria-label="Contatore chiamate senza risposta">
              {applicant.missedCalls.counter === 0 && (
                <Badge
                  label={applicant.missedCalls.counter.toString()}
                  size="XS"
                  color="primary"
                  aria-label={`${applicant.missedCalls.counter} chiamate senza risposta`}
                />
              )}
              {applicant.missedCalls.counter > 0 && (
                <Popover
                  aria-label="Visualizza chiamate senza risposta"
                  trigger={(
                    <Badge
                      label={applicant.missedCalls.counter.toString()}
                      size="XS"
                      color="neutral"
                      aria-label={`${applicant.missedCalls.counter} ${pluralizeWord('chiamat', 'a', 'e', applicant.missedCalls.counter)} senza risposta`}
                    />
                  )}
                  hasArrow
                >
                  <div css={styles.missedCallRegistryPopoverWrapper}>
                    <ApplicantMissedCallRegistryList missedCalls={applicant.missedCalls} />
                  </div>
                </Popover>
              )}
            </div>
          ),
          <ActionIcon
            aria-label="Visualizza candidatura"
            label="Visualizza"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToJobApplicationPage}
          />,
        ]}
      >
        <ViewApplicantPopoverPreview
          applicantId={jobApplication.applicantId}
          isFranchising={isFranchising}
          trigger={(
            <UserPreview
              avatar={{ placeholder: ICON_BRIEFCASE_OUTLINE, icon: !isFranchising ? ThemeIcons.DOVE : undefined }}
              size="S"
              name={applicant?.name}
            >
              {applicant?.phoneNumber || applicant?.email}
            </UserPreview>
          )}
        />
      </Card.Header>
      <Card.Content>
        <Stack gap={150}>
          <div>
            <UpdateJobApplicationStatus
              jobApplication={jobApplication}
              size="XS"
              onSuccess={onJobApplicationUpdate}
            />
          </div>
          <Grid gutter={75}>
            <Grid.Unit size={1 / 2}>
              <DetailStack
                label="Posizione"
                aria-label="Posizione candidatura"
              >
                {jobPositionData.data?.name ?? NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>
            <Grid.Unit size={1 / 2}>
              {(!provinces || provinces.length === 0) && (
                <DetailStack
                  label="Provincia"
                  aria-label="Provincia candidatura"
                >
                  {NO_VALUE_SYMBOL}
                </DetailStack>
              )}
              {provinces && provinces.length === 1 && (
                <DetailStack
                  label="Provincia"
                  aria-label="Provincia candidatura"
                >
                  {provinces[0]}
                </DetailStack>
              )}
              {provinces && provinces.length > 1 && (
                <Popover
                  aria-label="Visualizza tutte le province"
                  trigger={(
                    <DetailStack
                      label="Province"
                      aria-label="Province candidatura"
                    >
                      {`${provinces[0]},+${provinces.length - 1}`}
                    </DetailStack>
                  )}
                  hasArrow
                >
                  <div css={styles.provincesPopoverWrapper}>
                    <Stack gap={75}>
                      {provinces.map((province) => (
                        <Text.Caption key={province}>{province}</Text.Caption>
                      ))}
                    </Stack>
                  </div>
                </Popover>
              )}
            </Grid.Unit>
            <Grid.Unit size={1 / 2}>
              <DetailStack label="Aggiunta">
                <DateViewer
                  stringDate={jobApplication.createdAt!}
                  checkIfToday
                />
              </DetailStack>
            </Grid.Unit>
            <Grid.Unit size={1 / 2}>
              <Popover
                aria-label="Anteprima gestori candidatura"
                trigger={(
                  <div css={styles.managersPreviewWrapper}>
                    <UserPreview
                      size="S"
                      aria-label={supervisorName ?? 'Gestore HR non assegnato'}
                      // eslint-disable-next-line no-nested-ternary
                      avatar={supervisorName ? (supervisorPicture ? { src: supervisorPicture } : undefined) : { placeholder: ICON_ACCOUNT_OFF_OUTLINE }}
                    />
                    {jobApplication.assigneeEmail && jobApplication.applicationType === JobApplicationType.SALES && (
                      <ViewAgentSmallPreview
                        agentIdOrEmail={jobApplication.assigneeEmail}
                        aria-label="Anteprima assegnatario"
                        size="S"
                        omit={{ name: true }}
                      />
                    )}
                    {(!jobApplication.assigneeEmail || jobApplication.applicationType !== JobApplicationType.SALES) && (
                      <UserPreview
                        size="S"
                        aria-label={jobApplication.assigneeEmail ?? 'Assegnatario non definito'}
                        avatar={jobApplication.assigneeEmail ? undefined : { placeholder: ICON_ACCOUNT_OFF_OUTLINE }}
                      />
                    )}
                  </div>
                )}
                hasArrow
              >
                <div css={styles.managersPopoverWrapper}>
                  <Stack gap={100}>
                    <UserPreview
                      name={supervisorName ?? 'Non assegnato'}
                      size="S"
                      aria-label="Gestore HR"
                      avatar={supervisorPicture ? { src: supervisorPicture } : undefined}
                    >
                      Gestore HR
                    </UserPreview>
                    {jobApplication.assigneeEmail && jobApplication.applicationType === JobApplicationType.SALES && (
                      <ViewAgentSmallPreview
                        size="S"
                        agentIdOrEmail={jobApplication.assigneeEmail}
                        aria-label="Assegnatario"
                        details="Assegnatario"
                        hideOnError
                      />
                    )}
                    {jobApplication.assigneeEmail && jobApplication.applicationType !== JobApplicationType.SALES && (
                      <UserPreview
                        name={assigneeName ?? 'Non assegnato'}
                        size="S"
                        aria-label="Assegnatario"
                      >
                        Assegnatario
                      </UserPreview>
                    )}
                    {!jobApplication.assigneeEmail && (
                      <UserPreview
                        name="Non assegnato"
                        size="S"
                        aria-label="Assegnatario"
                      >
                        Assegnatario
                      </UserPreview>
                    )}
                  </Stack>
                </div>
              </Popover>
            </Grid.Unit>
          </Grid>

          {(calendarEvent || !!note) && (
            <Card.Divider />
          )}

          {calendarEvent && (
            <HStack
              gap={200}
              aria-label="Evento a calendario"
            >
              {calendarEvent.type === 'RECALL' ? (
                <DetailStack
                  label="Recall"
                  width="10rem"
                >
                  <DateViewer
                    stringDate={calendarEvent.date}
                    checkIfToday
                    checkIfTomorrow
                    withHour={isFuture(new Date(calendarEvent.date))}
                  />
                </DetailStack>
              ) : (
                <ViewHRInterviewPopoverPreview
                  hrInterview={calendarEvent as HRInterview}
                  applicationType={jobApplication.applicationType}
                  applicantId={applicant.id!}
                  aria-label="Visualizza dettagli colloquio"
                  trigger={(
                    <DetailStack
                      label={(calendarEvent as HRInterview).isRemote ? 'Colloquio da remoto' : 'Colloquio di presenza'}
                      width="10rem"
                    >
                      <DateViewer
                        stringDate={calendarEvent.date}
                        checkIfToday
                        checkIfTomorrow
                        withHour={isFuture(new Date(calendarEvent.date))}
                      />
                    </DetailStack>
                  )}
                />
              )}
              {calendarEvent.type === 'RECALL' ? (
                <UpdateHRRecallStatus
                  hrRecall={calendarEvent as HRRecall}
                  onSuccess={onRecallUpdate}
                />
              ) : (
                <UpdateHRInterviewStatus
                  hrInterview={calendarEvent as HRInterview}
                  onSuccess={onInterviewUpdate}
                />
              )}
            </HStack>
          )}

          {note && (
            <TextPreview
              text={note.text}
              author={note.author}
              date={note.date}
            />
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default ViewSerpJobApplicationCard;
